import { IColumnDefinition, ITableGroup } from '@common/types';
import { FC, useRef, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  Collapse,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  IconButton,
  TableContainer,
  TableSortLabel,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import { theme, useReportStore } from '@store';
import { SearchOff } from '@mui/icons-material';
import { t } from 'i18next';
import React from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useGroupsTable } from './useGroupsTable.tsx';
import { PatientsTable } from '../../PatientsTable.tsx';
import { UsersTable } from '../../UsersTable.tsx';
import { RecordsTable } from '../../RecordsTable.tsx';
import { TasksTable } from '../../TasksTable.tsx';
import { reportingTableRowNameFormat } from '@util/reportingTableRowNameFormat.ts';
import { formatCount } from '@util/number';

interface IGroupsTableProps {
  columns: IColumnDefinition[];
  rowOnClick?: (id: string) => void;
}

export const GroupsTable: FC<IGroupsTableProps> = ({ columns, rowOnClick }) => {
  const { report } = useReportStore();
  const {
    groups,
    filters,
    loading,
    totalCount,
    totalItemsCount,
    onPageSizeChange,
    handleChangeOrder,
    onPageIndexChange,
    tableRef,
    footerWidth,
    handleToggleGroup,
    openGroup,
  } = useGroupsTable();

  const { sort_by, sort_order } = filters;

  const stableCountRef = useRef(totalCount);
  const stableCount = useMemo(() => {
    if (!loading) {
      stableCountRef.current = totalCount;
    }
    return stableCountRef.current;
  }, [loading, totalCount]);

  const cellStyle = {
    width: '300px',
    minWidth: '300px',
    maxWidth: '300px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    textAlign: 'left',
    paddingY: '1rem',
    background: 'unset',
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
      }}
    >
      <TableContainer
        component={Paper}
        ref={tableRef}
        sx={{
          maxHeight: `calc(100vh - 480px)`,
          borderRadius: 0,
          boxShadow: 'none',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          borderBottom: 'none',
          borderTop: 'none',
        }}
      >
        <Table stickyHeader size='small'>
          {report?.report_type !== 'patients_report' && (
            <TableHead>
              <TableRow
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: theme.palette.headerBackground.main,
                  },
                }}
              >
                {columns.map((col, index) => (
                  <TableCell key={`${col.id}-${index}`} sx={{ ...cellStyle }}>
                    {col.sortable ? (
                      <TableSortLabel
                        active={true}
                        direction={
                          col.id === sort_by
                            ? (sort_order?.toLowerCase() as 'asc' | 'desc')
                            : 'asc'
                        }
                        onClick={() => handleChangeOrder(col.id as string)}
                      >
                        {col.label}
                      </TableSortLabel>
                    ) : (
                      col.label
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {groups.length > 0 &&
              groups.map(group => (
                <CollapsibleGroupTable
                  key={group.name ?? 'others'}
                  group={group}
                  isOpen={openGroup === group.name}
                  onToggle={handleToggleGroup}
                  rowOnClick={rowOnClick}
                  footerWidth={footerWidth}
                />
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {groups.length === 0 && (
        <Box
          sx={{
            bgcolor: theme.palette.common.white,
            display: 'grid',
            placeItems: 'center',
            width: '100%',
            height: 'calc(100vh - 500px)',
            borderLeft: '1px solid rgba(0, 0, 0, 0.12)',
            borderRight: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          {loading ? (
            <CircularProgress size='64px' />
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
              }}
            >
              <SearchOff
                sx={{ width: 46, height: 46, mb: 3 }}
                color='primary'
              />
              <Typography variant='h6'>
                {t('reporting.report.table.noResults.title')}
              </Typography>
              <Typography variant='body2' sx={{ color: 'text.disabled' }}>
                {t('reporting.report.table.noResults.subTitle')}
              </Typography>
            </Box>
          )}
        </Box>
      )}
      <Box
        sx={{
          borderBottom: 0,
          paddingY: '0.25rem',
          px: '1rem',
          zIndex: 100,
          bgcolor: 'white',
          border: theme => `1px solid ${theme.palette.grey[300]}`,
          left: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          variant='body2'
          sx={{
            fontWeight: 'medium',
            fontSize: '14px',
            color: theme.palette.semiBlack.main,
            width: '100%',
          }}
        >
          {formatCount(totalItemsCount)} total records
        </Typography>

        <TablePagination
          component='div'
          count={stableCount}
          page={filters.page! - 1}
          rowsPerPage={filters.limit!}
          onPageChange={onPageIndexChange}
          onRowsPerPageChange={onPageSizeChange}
        />
      </Box>
    </Box>
  );
};

interface ICollapsibleGroupTableProps {
  group: ITableGroup;
  isOpen: boolean;
  onToggle: (groupName: string | null) => void;
  rowOnClick?: (id: string) => void;
  footerWidth?: number | string;
}

const CollapsibleGroupTable = ({
  group,
  isOpen,
  onToggle,
  rowOnClick,
  footerWidth,
}: ICollapsibleGroupTableProps) => {
  const { report } = useReportStore();

  return (
    <React.Fragment>
      <TableRow
        sx={{
          '& > *': { borderBottom: 'unset' },
          width: '100%',
          cursor: 'pointer',
          transition: 'background-color 0.3s, transform 0.2s',
        }}
        onClick={() => onToggle(group.name)}
      >
        <TableCell colSpan={100} sx={{ p: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              bgcolor: theme.palette.grey[100],
              px: 2,
              py: 1,
            }}
          >
            <IconButton size='small'>
              {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            <Typography sx={{ flex: 1 }}>
              {`${group.name === null ? 'Others' : reportingTableRowNameFormat(group.name)} (${group.item_count})`}
            </Typography>
          </Box>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={100} sx={{ p: 0 }}>
          <Collapse in={isOpen} timeout={300} unmountOnExit>
            {(() => {
              switch (report?.report_type) {
                case 'patients_report':
                  return (
                    <PatientsTable
                      groupName={group.name}
                      footerWidth={footerWidth}
                    />
                  );
                case 'users_report':
                  return (
                    <UsersTable
                      groupName={group.name}
                      footerWidth={footerWidth}
                    />
                  );
                case 'records_report':
                  return (
                    <RecordsTable
                      groupName={group.name}
                      footerWidth={footerWidth}
                    />
                  );
                case 'tasks_report':
                  return (
                    <TasksTable
                      groupName={group.name}
                      footerWidth={footerWidth}
                      rowOnClick={rowOnClick}
                    />
                  );
                default:
                  return <></>;
              }
            })()}
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
};
